import { Badge, Button, Heading, Pane, Text, TextInputField } from 'evergreen-ui';
import moment from 'moment';
import { useState } from 'react';

/**
 * A reusable component for displaying and adding comments/activity.
 *
 * Props:
 * - activity: Array of activity items with { activityType, activityData, createdAt, createdBy }
 * - onAddComment: Function to call when adding a new comment (receives the activity object)
 * - title: Optional title for the section (default: "Comentarii")
 * - showSystemComments: Optional boolean to show system comments by default (default: false)
 * - maxVisibleComments: Optional number of comments to show before "See more" (default: 2)
 */
export default function CommentaryList(props) {
    const {
        activity = [],
        onAddComment,
        title = 'Comentarii',
        maxVisibleComments = 2,
    } = props;

    const [comment, setComment] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [showAllComments, setShowAllComments] = useState(false);
    const [showSystemComments, setShowSystemComments] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const text = (comment || '').trim();
        if (!text) return;

        const newActivity = {
            activityType: 'comment',
            activityData: { message: text },
        };

        if (typeof onAddComment === 'function') {
            try {
                setSubmitting(true);
                await onAddComment(newActivity);
                setComment('');
            } finally {
                setSubmitting(false);
            }
        } else {
            // Handler not provided - just clear the input
            setComment('');
        }
    };

    // Sort activity by date (newest first)
    const sortedActivity = [...activity].sort((a, b) =>
        new Date(b.createdAt) - new Date(a.createdAt)
    );

    // Separate user comments and system comments
    const userComments = sortedActivity.filter(item => item.activityType === 'comment');
    const systemComments = sortedActivity.filter(item => item.activityType !== 'comment');

    // Determine how many user comments to show
    const visibleUserComments = showAllComments
        ? userComments
        : userComments.slice(0, maxVisibleComments);

    const hasMoreComments = userComments.length > maxVisibleComments;
    const remainingCommentsCount = userComments.length - maxVisibleComments;

    const renderComment = (item, index, isSystemComment = false) => (
        <Pane
            key={`${isSystemComment ? 'sys' : 'user'}-${index}`}
            padding={12}
            marginBottom={8}
            background={isSystemComment ? 'tint1' : 'blueTint'}
            borderRadius={8}
            border={isSystemComment ? '1px solid #E4E7EB' : '1px solid #C1C4D6'}
            maxWidth={600}
            overflowWrap="break-word"
        >
            <Pane display="flex" alignItems="center" marginBottom={4}>
                <Badge color={isSystemComment ? 'neutral' : 'blue'} marginRight={8}>
                    {isSystemComment ? item.activityType : 'Comentariu'}
                </Badge>
                <Text size={300} color="muted">
                    {moment(item.createdAt).format('DD MMM YYYY, HH:mm')}
                </Text>
            </Pane>
            {item.activityData && item.activityData.message && (
                <Text size={400}>{item.activityData.message}</Text>
            )}
            {item.activityData && !item.activityData.message && (
                <Text size={400} color="muted">
                    {JSON.stringify(item.activityData)}
                </Text>
            )}
        </Pane>
    );

    return (
        <Pane>
            <Heading size={500} marginBottom={12}>{title}</Heading>

            {/* Add comment input */}
            <Pane marginBottom={16}>
                <form onSubmit={handleSubmit} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <TextInputField
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        placeholder="Scrie un comentariu..."
                        width="100%"
                        marginBottom={0}
                    />
                    <Button
                        type="submit"
                        disabled={submitting || !(comment || '').trim()}
                        isLoading={submitting}
                        appearance="primary"
                    >
                        {submitting ? 'Se trimite...' : 'Adaugă'}
                    </Button>
                </form>
            </Pane>

            {/* User comments list */}
            <Pane>
                {visibleUserComments.map((item, index) => renderComment(item, index, false))}
            </Pane>

            {/* See more button */}
            {hasMoreComments && !showAllComments && (
                <Pane marginBottom={12}>
                    <Button
                        appearance="minimal"
                        onClick={() => setShowAllComments(true)}
                    >
                        Vezi mai multe ({remainingCommentsCount} comentarii)
                    </Button>
                </Pane>
            )}

            {/* Show less button when all comments are visible */}
            {showAllComments && hasMoreComments && (
                <Pane marginBottom={12}>
                    <Button
                        appearance="minimal"
                        onClick={() => setShowAllComments(false)}
                    >
                        Arată mai puține
                    </Button>
                </Pane>
            )}

            {/* System comments toggle */}
            {systemComments.length > 0 && (
                <Pane marginTop={8}>
                    <Button
                        appearance="minimal"
                        onClick={() => setShowSystemComments(!showSystemComments)}
                    >
                        {showSystemComments
                            ? 'Ascunde mesajele de sistem'
                            : `Vezi mesajele de sistem (${systemComments.length})`}
                    </Button>
                </Pane>
            )}

            {/* System comments list */}
            {showSystemComments && systemComments.length > 0 && (
                <Pane marginTop={12}>
                    {systemComments.map((item, index) => renderComment(item, index, true))}
                </Pane>
            )}

            {/* Empty state */}
            {userComments.length === 0 && !showSystemComments && (
                <Pane padding={16} textAlign="center">
                    <Text color="muted">Nu există comentarii.</Text>
                </Pane>
            )}
        </Pane>
    );
}
